import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmployeeAssignment } from "./EmployeeAssignment";
import { AssignmentRuleset, Employee } from "../types";
import type { FilterGroup } from "@/utils/employeeGroups";

const criterionChipClass =
  "rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground";
const priorityBadgeClass =
  "rounded-full border bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums";

const propertyTypesDisplayNames = {
  studio: "Apartaestudio",
  apartment: "Apartamento",
  warehouse: "Bodega",
  house: "Casa",
  medical_office: "Consultorio",
  building: "Edificio",
  farm: "Finca",
  room: "Habitación",
  hotel: "Hotel",
  commercial_space: "Local",
  lot: "Lote",
  office: "Oficina",
  parking: "Parqueadero",
};

type RuleCardProps = {
  assignmentRuleset: AssignmentRuleset;
  employees: Employee[];
  onEditRule: (rule: AssignmentRuleset) => void;
  onAddEmployees: (assignmentRulesetId: string, employeeIds: string[]) => void;
  onRemoveEmployees: (assignmentRulesetId: string, employeeIds: string[]) => void;
  pipelines?: Array<{ id: string; name: string }>;
  employeeGroups?: FilterGroup[];
};

export const RuleCard = ({
  assignmentRuleset,
  employees,
  onEditRule,
  onAddEmployees,
  onRemoveEmployees,
  pipelines,
  employeeGroups,
}: RuleCardProps) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="text-base font-semibold text-foreground">{assignmentRuleset.name}</h4>
          <span className={priorityBadgeClass}>
            Prioridad {assignmentRuleset.priority}
          </span>
        </div>
        <Button
          variant="outline"
          onClick={() => onEditRule(assignmentRuleset)}
          className="min-h-11"
        >
          <Pencil className="mr-1 size-4" />
          Editar
        </Button>
      </div>

      <div className="flex flex-col items-start gap-4">
        {assignmentRuleset.ruleset && (
          <div className="flex flex-col items-start gap-2">
            <div className="flex flex-col flex-wrap items-start gap-2">
              {assignmentRuleset.ruleset.zones && assignmentRuleset.ruleset.zones.length > 0 && (
                <div className="flex flex-col items-start gap-1">
                  <span className="text-xs font-medium text-muted-foreground">Zonas:</span>
                  <div className="flex max-w-full flex-wrap gap-1 overflow-hidden">
                    {assignmentRuleset.ruleset.zones.map((zone, index) => (
                      <span
                        key={index}
                        className={`${criterionChipClass} max-w-[150px] truncate`}
                      >
                        {zone}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {assignmentRuleset.ruleset.price && (
                <span className={criterionChipClass}>
                  Precio:{" "}
                  {assignmentRuleset.ruleset.price.min
                    ? `$${assignmentRuleset.ruleset.price.min.toLocaleString()}`
                    : "Sin mínimo"}{" "}
                  –{" "}
                  {assignmentRuleset.ruleset.price.max
                    ? `$${assignmentRuleset.ruleset.price.max.toLocaleString()}`
                    : "Sin máximo"}
                </span>
              )}
              {assignmentRuleset.ruleset.department && (
                <span className={criterionChipClass}>
                  Operación:{" "}
                  {assignmentRuleset.ruleset.department === "rent" ? "Arriendo" : "Venta"}
                </span>
              )}
              {assignmentRuleset.ruleset.daysUntilMoveIn && (
                <span className={criterionChipClass}>
                  Días para mudarse: {assignmentRuleset.ruleset.daysUntilMoveIn.min} –{" "}
                  {assignmentRuleset.ruleset.daysUntilMoveIn.max}
                </span>
              )}
              {assignmentRuleset.ruleset.appointmentType && (
                <span className={criterionChipClass}>
                  Tipo de cita:{" "}
                  {assignmentRuleset.ruleset.appointmentType === "in-person"
                    ? "Presencial"
                    : "Virtual"}
                </span>
              )}
              {assignmentRuleset.ruleset.branchName && (
                <span className={criterionChipClass}>
                  Sucursal: {assignmentRuleset.ruleset.branchName}
                </span>
              )}
              {assignmentRuleset.ruleset.pipelineId && (
                <span className={criterionChipClass}>
                  Pipeline:{" "}
                  {pipelines?.find((p) => p.id === assignmentRuleset.ruleset.pipelineId)?.name ||
                    assignmentRuleset.ruleset.pipelineId}
                </span>
              )}

              {assignmentRuleset.ruleset.propertyTypes &&
                assignmentRuleset.ruleset.propertyTypes.length > 0 && (
                  <span className={`${criterionChipClass} break-words`}>
                    Tipos de propiedad:{" "}
                    {assignmentRuleset.ruleset.propertyTypes
                      .map(
                        (type) =>
                          propertyTypesDisplayNames[
                            type as keyof typeof propertyTypesDisplayNames
                          ] || type
                      )
                      .join(", ")}
                  </span>
                )}
              {assignmentRuleset.ruleset.transferCategories &&
                assignmentRuleset.ruleset.transferCategories.length > 0 && (
                  <span className={`${criterionChipClass} break-words`}>
                    Categorías de transferencia:{" "}
                    {assignmentRuleset.ruleset.transferCategories.join(", ")}
                  </span>
                )}
              {assignmentRuleset.ruleset.matchListingOwner && (
                <span className={criterionChipClass}>Solo inmuebles del agente</span>
              )}
              {assignmentRuleset.ruleset.listingSource && (
                <span className={criterionChipClass}>
                  Origen:{" "}
                  {assignmentRuleset.ruleset.listingSource === "mls"
                    ? "MLS"
                    : "Propios (directos)"}
                </span>
              )}
            </div>
          </div>
        )}

        <EmployeeAssignment
          assignedEmployees={assignmentRuleset.assignedEmployees}
          availableEmployees={employees}
          groups={employeeGroups}
          onAddEmployees={(employeeIds) => onAddEmployees(assignmentRuleset.id, employeeIds)}
          onRemoveEmployees={(employeeIds) => onRemoveEmployees(assignmentRuleset.id, employeeIds)}
        />
      </div>
    </div>
  );
};
